import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useCartApi } from "./useCartApi";
import { PLATFORMS } from "../../../../data/cart";
import { 
  selectCartDisplayTotals,
  buildAddToCartPayload,
  buildAddToCartPayloadForTypeChange,
  resolveProductTaxCode,
  transformProductToCartItem,
  parseQty,
  resolveCartItemVatRate,
  getCartItemNormalizedFields,
  applyStickyBundleDiscountPolicy,
  createStickyBundleDiscountState,
  resetStickyBundleDiscountState,
  isRhpEposTenant,
} from "../utils/cartCalculations";
import { storeSplitDataOnAdd } from "../../../../services/CartSplitHelper";
import productSplitService from "../../../../services/ProductSplitService";
import { isCartLineOfferDisplayRole } from "../../Promotion/utils/promotionOfferProductsShared";
import {
  getRequestedCartLineQuantity,
  validateMaximumOrderQuantityForCartAdd,
  validateMaximumOrderQuantityForLineQty,
} from "../../common/maximumOrderQuantity";


const ensureString = (value) => {
  if (value === undefined || value === null) return "";
  return String(value);
};

const buildOverrideKey = (item) => {
  if (!item) return "";
  const baseCandidate = item.product_id ?? item.productId ?? item.id ?? item.cart_item_id ?? null;
  if (baseCandidate === undefined || baseCandidate === null || baseCandidate === "") {
    return "";
  }
  const baseKey = ensureString(baseCandidate);
  const orderType = item.order_type || item.type || "";
  const role = isCartLineOfferDisplayRole(item) ? "offer" : "paid";
  if (orderType) return `${baseKey}__${orderType}__${role}`;
  return `${baseKey}__${role}`;
};

const getLegacyOverrideKeys = (item) => {
  if (!item) return [];
  const keys = [];
  const candidates = [item.product_id, item.productId, item.id, item.cart_item_id];
  candidates.forEach((candidate) => {
    const str = ensureString(candidate);
    if (str) {
      keys.push(str);
    }
  });
  return Array.from(new Set(keys));
};

/**
 * Enhanced cart logic hook with discount calculations
 * @param {number|string} customerId - Customer ID
 * @param {number} orderDiscountValue - Order discount value (percentage or fixed amount, default: 0)
 * @param {string} platform - Platform identifier ("5" for TELE_CALLING or "4" for POS). Defaults to "5"
 * @param {string} orderDiscountType - Order discount type ("percentage" or "fixed"/"amount", default: "percentage")
 * @returns {Object} Cart state and methods
 */
export const useEnhancedCartLogic = (customerId, orderDiscountValue = 0, platform = PLATFORMS.TELE_CALLING, orderDiscountType = "percentage", onErrorMessage = null, useTelecallerVatBreakdown = false) => {
  const {
    cartData,
    cartResponse,
    isLoadingCart,
    isFetchingCart,
    isCartMutating,
    addToCart,
    deleteCartItem,
    clearCart,
    pendingOperations,
    refetchCart
  } = useCartApi(customerId, platform, onErrorMessage);

  const [manualOverrides, setManualOverrides] = useState({});
  const storageKey = customerId ? `cartManualOverrides_${customerId}` : null;
  const categoryCacheKey = customerId ? `cartProductCategories_${customerId}` : null;

  const [productCategoryCache, setProductCategoryCache] = useState({});
  const [productLabelCache, setProductLabelCache] = useState({});

  useEffect(() => {
    if (!categoryCacheKey || typeof window === "undefined") return;
    try {
      const stored = localStorage.getItem(categoryCacheKey);
      if (stored) setProductCategoryCache(JSON.parse(stored));
    } catch {}
  }, [categoryCacheKey]);

  useEffect(() => {
    if (!Array.isArray(cartData) || cartData.length === 0) return;
    setProductLabelCache((prev) => {
      let next = prev;
      let changed = false;
      cartData.forEach((item) => {
        const pid = item?.product_id;
        if (pid === undefined || pid === null || pid === "") return;
        const key = String(pid);
        const code = String(item.product_code || item.code || "").trim();
        const name = String(item.product_name || item.name || item.desc || "").trim();
        if (!code && !name) return;
        const existing = next[key];
        if (existing?.code === code && existing?.name === name) return;
        if (!changed) {
          next = { ...prev };
          changed = true;
        }
        next[key] = {
          code: code || existing?.code || "",
          name: name || existing?.name || "",
        };
      });
      return changed ? next : prev;
    });
  }, [cartData]);

  const getOverrideKey = useCallback((item) => buildOverrideKey(item), []);

  const cacheProductCategory = useCallback((product) => {
    if (!product) return;
    const pid = product.product_id ?? product.id;
    const cat = product.categorie_name ?? product.category_name ?? product.category;
    if (!pid || !cat) return;
    setProductCategoryCache((prev) => {
      const next = { ...prev, [String(pid)]: String(cat).trim() };
      if (categoryCacheKey && typeof window !== "undefined") {
        localStorage.setItem(categoryCacheKey, JSON.stringify(next));
      }
      return next;
    });
  }, [categoryCacheKey]);

  const cacheProductDisplay = useCallback((product) => {
    if (!product) return;
    const pid = product.product_id ?? product.id;
    if (pid === undefined || pid === null || pid === "") return;
    const code = String(product.product_code || product.code || "").trim();
    const name = String(product.product_name || product.name || product.desc || "").trim();
    const taxCode = String(
      product.tax_code ?? product.taxCode ?? product.vat_code ?? ""
    ).trim();
    if (!code && !name && !taxCode) return;
    setProductLabelCache((prev) => {
      const key = String(pid);
      const existing = prev[key];
      if (
        existing?.code === code &&
        existing?.name === name &&
        existing?.tax_code === taxCode
      ) {
        return prev;
      }
      return {
        ...prev,
        [key]: {
          code: code || existing?.code || "",
          name: name || existing?.name || "",
          tax_code: taxCode || existing?.tax_code || "",
        },
      };
    });
  }, []);

  useEffect(() => {
    if (!storageKey || typeof window === "undefined") {
      setManualOverrides({});
      return;
    }

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        setManualOverrides(parsed && typeof parsed === "object" ? parsed : {});
      } else {
        setManualOverrides({});
      }
    } catch (error) {
      setManualOverrides({});
    }
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey || typeof window === "undefined") return;
    if (!cartData || !Array.isArray(cartData)) {
      return;
    }
    // Skip while the cart is still loading: cartData is briefly [] before
    // the real response arrives, which would otherwise be indistinguishable
    // from a genuinely empty cart below and wipe every override.
    if (isLoadingCart) return;

    setManualOverrides((prev) => {
      let changed = false;
      const next = { ...(prev || {}) };

      cartData.forEach((item) => {
        const key = getOverrideKey(item);
        if (!key) return;
        if (isCartLineOfferDisplayRole(item)) return;

        const apiDiscount = parseFloat(
        item.productWiseDiscountValue !== undefined &&
          item.productWiseDiscountValue !== null &&
          item.productWiseDiscountValue !== ""
          ? item.productWiseDiscountValue
          : (item.item_discount ?? item.discount ?? 0)
      );
        const apiDiscountType = item.productWiseDiscountType || item.discount_type || "amount";
        const hasApiDiscount = Number.isFinite(apiDiscount);
        
        const currentOverride = next[key];
        const currentDiscount = currentOverride?.discount;
        
        if (hasApiDiscount && apiDiscount > 0) {
          const apiDiscountFormatted = apiDiscount.toFixed(2);
          const currentDiscountType = currentOverride?.discount_type;
          
          if (!currentOverride || currentDiscount !== apiDiscountFormatted || currentDiscountType !== apiDiscountType) {
            next[key] = {
              ...(currentOverride || {}),
              discount: apiDiscountFormatted,
              discount_type: apiDiscountType
            };
            changed = true;
          }
        } else if (hasApiDiscount && apiDiscount === 0 && currentOverride) {
          if (
            currentOverride.discount !== undefined ||
            currentOverride.discount_type !== undefined ||
            currentOverride.discount_comment !== undefined
          ) {
            const { discount: _d, discount_type: _dt, discount_comment: _dc, ...rest } = currentOverride;
            if (Object.keys(rest).length === 0) {
              delete next[key];
            } else {
              next[key] = rest;
            }
            changed = true;
          }
        }
      });

      if (!changed) return prev;

      if (storageKey && typeof window !== "undefined") {
        localStorage.setItem(storageKey, JSON.stringify(next));
      }

      return next;
    });
  }, [cartData, storageKey, getOverrideKey, isLoadingCart]);

  useEffect(() => {
    if (!storageKey || typeof window === "undefined") return;
    if (!cartData || !Array.isArray(cartData)) {
      return;
    }
    // Same reasoning as the effect above: don't treat a still-loading
    // (momentarily empty) cartData as "no items have this product anymore"
    // and delete overrides that are actually still valid.
    if (isLoadingCart) return;

    setManualOverrides((prev) => {
      if (!prev || Object.keys(prev).length === 0) return prev;

      let changed = false;
      const next = {};
      const validKeys = new Set();

      cartData.forEach((item) => {
        const primaryKey = getOverrideKey(item);
        if (!primaryKey) return;
        validKeys.add(primaryKey);

        const legacyKeys = getLegacyOverrideKeys(item);
        let storedValue;

        if (prev[primaryKey] !== undefined) {
          storedValue = prev[primaryKey];
        } else if (!isCartLineOfferDisplayRole(item)) {
          for (let i = 0; i < legacyKeys.length; i += 1) {
            const legacyKey = legacyKeys[i];
            if (prev[legacyKey] !== undefined) {
              storedValue = prev[legacyKey];
              changed = true;
              break;
            }
          }
        }

        if (storedValue !== undefined) {
          next[primaryKey] = storedValue;
        }
      });

      Object.keys(prev).forEach((key) => {
        if (!validKeys.has(key)) {
          changed = true;
        }
      });

      if (!changed) {
        return prev;
      }

      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  }, [cartData, storageKey, getOverrideKey, isLoadingCart]);

  const updateOverrides = useCallback((builder, options = {}) => {
    const { persist = true } = options;
    setManualOverrides((prev) => {
      const next = builder(prev);
      if (!next) {
        return prev;
      }

      if (persist && storageKey && typeof window !== "undefined") {
        localStorage.setItem(storageKey, JSON.stringify(next));
      }

      return next;
    });
  }, [storageKey]);

  const baseCartItemsCacheRef = useRef({
    manualOverrides: null,
    productCategoryCache: null,
    productLabelCache: null,
    byItem: new WeakMap(),
  });

  const baseCartItems = useMemo(() => {
    if (!cartData || !Array.isArray(cartData)) {
      return [];
    }

    const itemCache = baseCartItemsCacheRef.current;
    if (
      itemCache.manualOverrides !== manualOverrides ||
      itemCache.productCategoryCache !== productCategoryCache ||
      itemCache.productLabelCache !== productLabelCache
    ) {
      itemCache.manualOverrides = manualOverrides;
      itemCache.productCategoryCache = productCategoryCache;
      itemCache.productLabelCache = productLabelCache;
      itemCache.byItem = new WeakMap();
    }

    return cartData.map((item) => {
      if (item && itemCache.byItem.has(item)) {
        return itemCache.byItem.get(item);
      }

      const key = getOverrideKey(item);
      const isOfferLine = isCartLineOfferDisplayRole(item);
      const legacyKeys = getLegacyOverrideKeys(item);
      let overrides = {};
      if (key && manualOverrides[key]) {
        overrides = manualOverrides[key];
      } else if (!isOfferLine) {
        const baseCandidate = item.product_id ?? item.productId ?? item.id ?? item.cart_item_id;
        const orderType = item.order_type || item.type || "";
        const typeKey = orderType ? `${ensureString(baseCandidate)}__${orderType}` : ensureString(baseCandidate);
        const fallbackKeys = [typeKey, ...legacyKeys];
        for (let i = 0; i < fallbackKeys.length; i += 1) {
          const candidate = fallbackKeys[i];
          if (candidate && candidate !== key && manualOverrides[candidate]) {
            overrides = manualOverrides[candidate];
            break;
          }
        }
      }
      
      const apiDiscount = parseFloat(
        item.productWiseDiscountValue !== undefined &&
          item.productWiseDiscountValue !== null &&
          item.productWiseDiscountValue !== ""
          ? item.productWiseDiscountValue
          : (item.item_discount ?? item.discount ?? 0)
      );
      const apiDiscountType = item.productWiseDiscountType || item.discount_type || "amount";
      const hasApiDiscount = Number.isFinite(apiDiscount);
      
      const apiIsRefund =
        item.is_refund === "1" ||
        item.is_refund === 1 ||
        item.is_refund === true ||
        parseFloat(item.price) < 0 ||
        parseFloat(item.unit_price) < 0;
      const priceOverride = overrides.price !== undefined ? overrides.price : item.price;
      const unitPriceOverride = overrides.price !== undefined ? overrides.price : (item.unit_price ?? item.price);
      let discountOverride;
      let discountTypeOverride;
      
      if (overrides.discount !== undefined && overrides.discount !== null && overrides.discount !== "") {
        discountOverride = overrides.discount;
        discountTypeOverride = overrides.discount_type !== undefined ? overrides.discount_type : (apiDiscountType || "amount");
      } else if (hasApiDiscount && apiDiscount > 0) {
        discountOverride = apiDiscount.toFixed(2);
        discountTypeOverride = apiDiscountType;
        if (key) {
          if (!overrides || Object.keys(overrides).length === 0) {
            overrides = {
              discount: discountOverride,
              discount_type: discountTypeOverride
            };
          } else {
            overrides = {
              ...overrides,
              discount: discountOverride,
              discount_type: discountTypeOverride
            };
          }
        }
      } else {
        discountOverride = hasApiDiscount ? apiDiscount.toFixed(2) : (item.productWiseDiscountValue || item.discount || "0.00");
        discountTypeOverride = apiDiscountType || "amount";
      }

      const hasBackendSubtotal = item.sub_total !== undefined && item.sub_total !== null && item.sub_total !== "";
      const backendSubtotal = hasBackendSubtotal ? parseFloat(item.sub_total) : null;
      const discNum = parseFloat(discountOverride);
      const keepComment = !isOfferLine && Number.isFinite(discNum) && discNum > 0.0001;
      const lineComment = keepComment
        ? String(overrides.discount_comment ?? item.discount_comment ?? item.comment ?? "").trim()
        : "";
      let lineOverrides = overrides;
      if (!keepComment && overrides?.discount_comment !== undefined) {
        const { discount_comment: _omitComment, ...restOverrides } = overrides;
        lineOverrides = restOverrides;
      }
      
      const originalProductWiseDiscountValue = item.productWiseDiscountValue !== undefined && item.productWiseDiscountValue !== null && item.productWiseDiscountValue !== "" 
        ? parseFloat(item.productWiseDiscountValue) 
        : (item.discount ? parseFloat(item.discount) : undefined);
      const originalProductWiseDiscountType = item.productWiseDiscountType || item.discount_type || "amount";
      
      const enrichedCategorieName = item.categorie_name ?? item.category_name ?? item.category ?? productCategoryCache[String(item.product_id)] ?? "";
      const cachedLabel = productLabelCache[String(item.product_id)] || {};
      const resolvedCode = item.product_code || item.code || cachedLabel.code || "";
      const resolvedName =
        item.product_name || item.name || item.desc || cachedLabel.name || "";
      const resolvedTaxCode =
        resolveProductTaxCode(item) || cachedLabel.tax_code || "";
      const resolvedVatRate = resolveCartItemVatRate(item);
      const transformed = {
        ...item,
        code: resolvedCode,
        product_code: resolvedCode,
        name: resolvedName,
        product_name: resolvedName,
        desc: resolvedName,
        tax_code: resolvedTaxCode,
        rate: item.rate ?? item.tax_rate ?? String(resolvedVatRate),
        tax_rate: item.tax_rate ?? item.rate ?? String(resolvedVatRate),
        categorie_name: enrichedCategorieName,
        category_name: enrichedCategorieName,
        category: enrichedCategorieName,
        price: priceOverride,
        unit_price: unitPriceOverride,
        discount: discountOverride,
        discount_type: discountTypeOverride,
        discount_comment: lineComment,
        comment: lineComment,
        is_refund:
          overrides.is_refund ??
          (apiIsRefund ? "1" : item.is_refund ?? "0"),
        is_contra: item.is_contra || "0",
        sales_box_price: item.sales_box_price,
        sales_piece_price: item.sales_piece_price,
        originalItem: item,
        manualOverrides: lineOverrides,
        productWiseDiscountType: originalProductWiseDiscountType,
        productWiseDiscountValue: originalProductWiseDiscountValue,
        subtotal: hasBackendSubtotal ? backendSubtotal : (item.subtotal || undefined),
        useBackendCalculations: hasBackendSubtotal
      };
      if (item) {
        itemCache.byItem.set(item, transformed);
      }
      return transformed;
    });
  }, [cartData, manualOverrides, getOverrideKey, productCategoryCache, productLabelCache]);

  const stickyBundleStateRef = useRef(null);
  if (!stickyBundleStateRef.current) {
    stickyBundleStateRef.current = createStickyBundleDiscountState();
  }

  const { bundleDiscounts, bundleLineDiscountByKey } = useMemo(() => {
    const rawBundleDiscounts = Array.isArray(cartResponse?.bundle_discounts)
      ? cartResponse.bundle_discounts
      : [];
    if (!useTelecallerVatBreakdown || rawBundleDiscounts.length === 0) {
      return {
        bundleDiscounts: rawBundleDiscounts,
        bundleLineDiscountByKey: null,
      };
    }

    const stickyResult = applyStickyBundleDiscountPolicy(
      rawBundleDiscounts,
      baseCartItems,
      stickyBundleStateRef.current
    );

    return {
      bundleDiscounts: stickyResult.bundleDiscounts,
      bundleLineDiscountByKey: stickyResult.lineDiscountByKey,
    };
  }, [cartResponse, baseCartItems, useTelecallerVatBreakdown]);

  const cartTotals = useMemo(
    () =>
      selectCartDisplayTotals(
        baseCartItems,
        orderDiscountValue,
        orderDiscountType,
        cartResponse,
        bundleDiscounts,
        useTelecallerVatBreakdown
      ),
    [
      baseCartItems,
      orderDiscountValue,
      orderDiscountType,
      cartResponse,
      bundleDiscounts,
      useTelecallerVatBreakdown,
    ]
  );

  const adjustedCartItemsCacheRef = useRef(new WeakMap());

  const adjustedCartItems = useMemo(() => {
    if (!baseCartItems || !Array.isArray(baseCartItems)) {
      return [];
    }

    const promoIdStr = (it) => {
      const raw = it?.promo_id ?? it?.promotion_id ?? it?.promosId ?? it?.promoId;
      if (raw === null || raw === undefined || raw === "") return "";
      const s = String(raw).trim();
      if (!s || s.toLowerCase() === "null" || s === "0") return "";
      return s;
    };
    const typeRank = (it) => {
      let t = String(it?.promo_type ?? it?.promoType ?? it?.promosType ?? "").trim().toUpperCase();
      if (!t && it?.buyOrGet) {
        const b = String(it.buyOrGet).trim().toUpperCase();
        if (b === "BUY" || b === "GET" || b === "FREE") t = b;
      }
      if (t === "BUY") return 0;
      if (t === "GET") return 1;
      if (t === "FREE") return 2;
      return 99;
    };

    const seenPromoIds = new Set();
    let hasGroupedPromoIds = false;
    for (const it of baseCartItems) {
      const pid = promoIdStr(it);
      if (!pid) continue;
      if (seenPromoIds.has(pid)) {
        hasGroupedPromoIds = true;
        break;
      }
      seenPromoIds.add(pid);
    }

    const sorted = hasGroupedPromoIds
      ? [...baseCartItems].sort((a, b) => {
          const pa = promoIdStr(a);
          const pb = promoIdStr(b);
          if (pa && pb && pa === pb) {
            const diff = typeRank(a) - typeRank(b);
            if (diff !== 0) return diff;
          }
          return 0;
        })
      : baseCartItems;

    const cache = adjustedCartItemsCacheRef.current;
    return sorted.map((item) => {
      const itemId = item.id || item.product_id;
      const invoiceDiscountAmount = cartTotals.itemDiscounts?.get(itemId) || 0;

      const cached = cache.get(item);
      if (cached && cached.invoiceDiscountAmount === invoiceDiscountAmount) {
        return cached.output;
      }

      const output = {
        ...item,
        invoiceDiscountAmount
      };
      cache.set(item, { invoiceDiscountAmount, output });
      return output;
    });
  }, [baseCartItems, cartTotals.itemDiscounts]);

  const addProductToCart = useCallback((product) => {
    if (!customerId) return;

    const incomingQty = getRequestedCartLineQuantity(product);
    const quantityValidation = validateMaximumOrderQuantityForCartAdd(
      product,
      adjustedCartItems,
      incomingQty
    );
    if (!quantityValidation.allowed) {
      if (typeof onErrorMessage === "function") {
        onErrorMessage(quantityValidation.message, "warning");
      }
      return;
    }

    cacheProductCategory(product);
    cacheProductDisplay(product);
    storeSplitDataOnAdd(customerId, product);

    const cartItem = transformProductToCartItem(
      {
        ...product,
        is_promoted: "0",
        promo_id: "",
        promotion_id: "",
        promo_type: "",
        promoType: "",
        promosType: "",
        generated: false,
        free_product: "N",
      },
      customerId
    );
    const payload = buildAddToCartPayload(cartItem, customerId);
    addToCart(payload, 300, true);
  }, [customerId, addToCart, cacheProductCategory, cacheProductDisplay, adjustedCartItems, onErrorMessage]);

  const handleQuantityChange = useCallback((cartItem, qtyValue, changeType = 'absolute') => {
    if (!customerId) return;
    const sourceItem = cartItem?.originalItem || cartItem;
    const currentQty = parseQty(sourceItem, sourceItem.qty);
    const itemToUse = cartItem || sourceItem;

    let payload;
    let targetQty = currentQty;
    const isIncrementDecrement = changeType === 'increment' || changeType === 'decrement';
    
    if (isIncrementDecrement) {
      if (!qtyValue) return;
      targetQty = currentQty + qtyValue;
      const quantityValidation = validateMaximumOrderQuantityForLineQty(
        sourceItem,
        adjustedCartItems,
        targetQty
      );
      if (!quantityValidation.allowed) {
        if (typeof onErrorMessage === "function") {
          onErrorMessage(quantityValidation.message, "warning");
        }
        return;
      }
      payload = buildAddToCartPayload(itemToUse, customerId, qtyValue);
    } else {
      const strVal = String(qtyValue);
      const hasDecimal = strVal.includes(".");
      targetQty = hasDecimal ? parseFloat(strVal) : parseQty(sourceItem, qtyValue);
      if (!Number.isFinite(targetQty)) return;
      if (targetQty <= 0) return;
      const quantityValidation = validateMaximumOrderQuantityForLineQty(
        sourceItem,
        adjustedCartItems,
        targetQty
      );
      if (!quantityValidation.allowed) {
        if (typeof onErrorMessage === "function") {
          onErrorMessage(quantityValidation.message, "warning");
        }
        return;
      }
      const qtyDelta = targetQty - currentQty;
      payload = buildAddToCartPayload(itemToUse, customerId, qtyDelta);
    }

    addToCart(payload, 500, isIncrementDecrement);
  }, [customerId, addToCart, adjustedCartItems, onErrorMessage]);

  const handleTypeChange = useCallback(async (cartItem, newType) => {
    if (!customerId) return;

    const sourceItem = cartItem?.originalItem || cartItem;
    const currentQty = parseQty(sourceItem, sourceItem.qty) || 1;
    const productId = sourceItem.product_id;
    const cartItemId = sourceItem.id;

    productSplitService.setDefaultOrderType(customerId, productId, newType);

    deleteCartItem(cartItemId);

    setTimeout(() => {
      const itemToUse = cartItem || sourceItem;
      const payload = buildAddToCartPayloadForTypeChange(itemToUse, customerId, newType, currentQty);
      addToCart(payload, 300);
    }, 100);
  }, [customerId, addToCart, deleteCartItem]);

  const handleAddAsNewType = useCallback((cartItem, newType, qty) => {
    if (!customerId) return;

    const sourceItem = cartItem?.originalItem || cartItem;
    const itemToUse = cartItem || sourceItem;
    const payload = buildAddToCartPayloadForTypeChange(itemToUse, customerId, newType, qty);
    payload.id = "";
    addToCart(payload, 300);
  }, [customerId, addToCart]);

  const handlePriceChange = useCallback((cartItem, newPrice, options = {}) => {
    const { commit = true } = options;
    const sourceItem = cartItem?.originalItem || cartItem;
    const key = getOverrideKey(sourceItem);
    if (!key) return;

    const legacyKeys = getLegacyOverrideKeys(sourceItem);
    const valueString = typeof newPrice === "number" ? newPrice.toString() : ensureString(newPrice);
    const parsedPrice = parseFloat(valueString);
    const hasValidNumber = Number.isFinite(parsedPrice) && (parsedPrice >= 0 || options.allowNegative === true);
    const originalNumber = parseFloat(sourceItem.price);
    const matchesOriginal = hasValidNumber && Number.isFinite(originalNumber) && Math.abs(parsedPrice - originalNumber) < 1e-6;
    const normalizedValue = valueString.trim();

    updateOverrides((prev) => {
      const next = { ...prev };
      let current = next[key];
      const overrides = { ...(current || {}) };
      let changed = false;

      legacyKeys.forEach((legacyKey) => {
        if (!legacyKey || legacyKey === key) return;
        if (next[legacyKey] !== undefined) {
          if (current === undefined) {
            current = next[legacyKey];
            Object.assign(overrides, next[legacyKey]);
          }
          delete next[legacyKey];
          changed = true;
        }
      });

      if (!commit) {
        if (overrides.price !== valueString) {
          overrides.price = valueString;
          changed = true;
        }
      } else if (!hasValidNumber) {
        if (overrides.price !== undefined) {
          delete overrides.price;
          changed = true;
        }
      } else {
        if (matchesOriginal && !options.isRefund) {
          if (overrides.price !== undefined) {
            delete overrides.price;
            changed = true;
          }
          if (overrides.is_refund !== undefined) {
            delete overrides.is_refund;
            changed = true;
          }
        } else if (overrides.price !== normalizedValue) {
          overrides.price = normalizedValue;
          changed = true;
        }
        if (options.isRefund) {
          if (overrides.is_refund !== "1") {
            overrides.is_refund = "1";
            changed = true;
          }
        }
      }

      if (!changed) return null;

      if (Object.keys(overrides).length === 0) {
        if (current !== undefined) {
          delete next[key];
        }
      } else {
        next[key] = overrides;
      }

      return next;
    }, { persist: commit });

    // Call add-to-cart API when price is committed (similar to quantity change)
    if (commit && hasValidNumber && customerId && (!matchesOriginal || options.isRefund)) {
      const itemToUse = cartItem || sourceItem;
      let discountType = "percentage";
      if (itemToUse.manualOverrides?.discount_type !== undefined && itemToUse.manualOverrides?.discount_type !== null && itemToUse.manualOverrides?.discount_type !== "") {
        discountType = itemToUse.manualOverrides.discount_type;
      } else if (itemToUse.discount_type !== undefined && itemToUse.discount_type !== null && itemToUse.discount_type !== "") {
        discountType = itemToUse.discount_type;
      }
      const discount = itemToUse.manualOverrides?.discount || itemToUse.discount;
      const updatedItem = {
        ...itemToUse,
        price: parsedPrice.toFixed(2),
        unit_price: parsedPrice.toFixed(2),
        list_box_price: parsedPrice.toFixed(2),
        is_refund: options.isRefund ? "1" : (itemToUse.is_refund || "0"),
        is_contra: itemToUse.is_contra || "0",
        discount_type: discountType,
        discount: discount,
        manualOverrides: { 
          ...(itemToUse.manualOverrides || {}),
          price: normalizedValue,
          discount_type: discountType,
          discount: discount,
          ...(options.isRefund ? { is_refund: "1" } : {}),
        }
      };
      const qtyForPayload = options.isRefund
        ? 0
        : isRhpEposTenant()
          ? parseQty(updatedItem, updatedItem.qty) || 1
          : 0;
      const payload = buildAddToCartPayload(updatedItem, customerId, qtyForPayload);
      addToCart(payload, 500);
    }
  }, [getOverrideKey, updateOverrides, customerId, addToCart]);

  const handleDiscountChange = useCallback((cartItem, newDiscount, discountType = "percentage") => {
    const sourceItem = cartItem?.originalItem || cartItem;
    const key = getOverrideKey(sourceItem);
    if (!key) return;

    const parsedDiscount = parseFloat(newDiscount);
    if (!Number.isFinite(parsedDiscount) || parsedDiscount < 0) return;

    const normalizedDiscountType = String(discountType).trim().toLowerCase();
    const isAmountDiscount =
      normalizedDiscountType === "amount" ||
      normalizedDiscountType === "£" ||
      normalizedDiscountType === "fixed";

    let boundedDiscount = parsedDiscount;
    if (normalizedDiscountType === "percentage") {
      boundedDiscount = Math.min(parsedDiscount, 100);
    } else if (isAmountDiscount) {
      const unitPrice = parseFloat(
        sourceItem.unit_price ??
          sourceItem.price ??
          (sourceItem.order_type === "Piece" || sourceItem.type === "Piece"
            ? sourceItem.offer_piece_price || sourceItem.sales_piece_price
            : sourceItem.offer_box_price || sourceItem.sales_box_price) ??
          0
      );
      if (Number.isFinite(unitPrice) && unitPrice > 0) {
        boundedDiscount = Math.min(parsedDiscount, unitPrice);
      }
    }

    const formattedDiscount = boundedDiscount.toFixed(2);
    const originalItem = sourceItem.originalItem || sourceItem;
    const originalValue = (() => {
      const originalNumber = parseFloat(originalItem.discount ?? 0);
      return Number.isFinite(originalNumber) ? originalNumber.toFixed(2) : "0.00";
    })();
    const originalDiscountType = originalItem.discount_type || "percentage";

    const currentOverride = cartItem?.manualOverrides ?? sourceItem?.manualOverrides;
    const currentOverrideDiscount = currentOverride?.discount;
    const currentOverrideDiscountType = currentOverride?.discount_type;
    const currentDiscountValue = currentOverrideDiscount !== undefined && currentOverrideDiscount !== null
      ? parseFloat(currentOverrideDiscount).toFixed(2)
      : originalValue;
    const currentDiscountTypeValue = currentOverrideDiscountType !== undefined && currentOverrideDiscountType !== null
      ? currentOverrideDiscountType
      : originalDiscountType;

    const discountValueChanged = currentDiscountValue !== formattedDiscount;
    const discountTypeChanged = currentDiscountTypeValue !== discountType;
    const isResettingToOriginal = formattedDiscount === originalValue && discountType === originalDiscountType && (currentOverrideDiscount !== undefined || currentOverrideDiscountType !== undefined);
    const isExplicitZeroUpdate = parsedDiscount === 0;
    const hasActualChange = discountValueChanged || discountTypeChanged || isResettingToOriginal || isExplicitZeroUpdate;

    if (!hasActualChange) {
      return;
    }

    updateOverrides((prev) => {
      const next = { ...(prev || {}) };
      let current = next[key];
      const overrides = { ...(current || {}) };
      let changed = false;

      const legacyKeys = getLegacyOverrideKeys(sourceItem);
      legacyKeys.forEach((legacyKey) => {
        if (!legacyKey || legacyKey === key) return;
        if (next[legacyKey] !== undefined) {
          if (current === undefined) {
            current = next[legacyKey];
            Object.assign(overrides, next[legacyKey]);
          }
          delete next[legacyKey];
          changed = true;
        }
      });

      if (formattedDiscount === originalValue && discountType === originalDiscountType) {
        if (overrides.discount !== undefined) {
          delete overrides.discount;
          changed = true;
        }
        if (overrides.discount_type !== undefined) {
          delete overrides.discount_type;
          changed = true;
        }
      } else {
        if (overrides.discount !== formattedDiscount) {
          overrides.discount = formattedDiscount;
          changed = true;
        }
        if (overrides.discount_type !== discountType) {
          overrides.discount_type = discountType;
          changed = true;
        }
      }

      if (parsedDiscount === 0 || formattedDiscount === "0.00") {
        if (overrides.discount_comment !== undefined) {
          delete overrides.discount_comment;
          changed = true;
        }
      }

      if (!changed) return null;

      if (Object.keys(overrides).length === 0) {
        if (current !== undefined) {
          delete next[key];
        }
      } else {
        next[key] = overrides;
      }

      return next;
    }, { persist: true });

    if (customerId && hasActualChange) {
      const itemToUse = cartItem || sourceItem;
      let apiDiscountType = "percentage";
      const normalizedDiscountType = String(discountType).trim().toLowerCase();
      if (normalizedDiscountType === "£") {
        apiDiscountType = "amount";
      } else if (normalizedDiscountType === "percentage") {
        apiDiscountType = "percentage";
      } else if (normalizedDiscountType === "amount") {
        apiDiscountType = "amount";
      } else {
        apiDiscountType = "percentage";
      }
      
      const updatedItem = {
        ...itemToUse,
        discount: formattedDiscount,
        discount_type: discountType,
        ...(parsedDiscount === 0
          ? { comment: "", discount_comment: "" }
          : {}),
        manualOverrides: {
          ...(itemToUse.manualOverrides || {}),
          discount: formattedDiscount,
          discount_type: discountType,
          ...(parsedDiscount === 0 ? { discount_comment: "" } : {}),
        }
      };
      const payload = buildAddToCartPayload(updatedItem, customerId, 0);
      payload.discount_type = apiDiscountType;
      payload.discount_value = formattedDiscount;
      if (parsedDiscount === 0) {
        payload.discount_comment = "";
      }
      addToCart(payload, 500);
    }
  }, [getOverrideKey, updateOverrides, customerId, addToCart]);

  // A short note explaining why a manual discount was given (e.g. "manager
  // approved", "damaged stock"). Mirrors handleDiscountChange's override +
  // sync pattern: stored locally in manualOverrides immediately (so it
  // displays right away), then synced to the backend via the same
  // ws-add-to-cart call discount value/type changes already use, as a new
  // discount_comment field - so it round-trips from the API like discount
  // itself does, rather than only existing locally until order placement.
  const handleDiscountCommentChange = useCallback((cartItem, newComment) => {
    if (isCartLineOfferDisplayRole(cartItem)) return;
    const sourceItem = cartItem?.originalItem || cartItem;
    const key = getOverrideKey(cartItem) || getOverrideKey(sourceItem);
    if (!key) return;

    const comment = String(newComment || "").trim();
    const currentOverride = cartItem?.manualOverrides ?? sourceItem?.manualOverrides;
    const currentComment = String(currentOverride?.discount_comment || sourceItem.discount_comment || "").trim();
    if (comment === currentComment) return;

    updateOverrides((prev) => {
      const next = { ...(prev || {}) };
      const current = next[key];
      const overrides = { ...(current || {}) };

      if (comment) {
        overrides.discount_comment = comment;
      } else {
        delete overrides.discount_comment;
      }

      if (Object.keys(overrides).length === 0) {
        if (current !== undefined) delete next[key];
      } else {
        next[key] = overrides;
      }
      return next;
    }, { persist: true });

    if (customerId) {
      const itemToUse = cartItem || sourceItem;
      const updatedItem = {
        ...itemToUse,
        discount_comment: comment,
        manualOverrides: {
          ...(itemToUse.manualOverrides || {}),
          discount_comment: comment,
        },
      };
      const payload = buildAddToCartPayload(updatedItem, customerId, 0);
      payload.discount_comment = comment;
      addToCart(payload, 500);
    }
  }, [getOverrideKey, updateOverrides, customerId, addToCart]);

  const handleDeleteItem = useCallback((id) => {
    if (!customerId) return;
    const cartItem = cartData?.find(item => String(item.id) === String(id));
    if (cartItem?.product_id) {
      productSplitService.clearProductType(customerId, cartItem.product_id);
    }
    deleteCartItem(id);
  }, [customerId, deleteCartItem, cartData]);

  const handleClearCart = useCallback(() => {
    resetStickyBundleDiscountState(stickyBundleStateRef.current);
    clearCart();
  }, [clearCart]);

  const getItemDetails = useCallback((item) => {
    const n = getCartItemNormalizedFields(item);
    return {
      isFree: n.isFree,
      isDiscounted: n.isDiscounted,
      isPromoted: n.isPromoted,
      isGetPromotion: n.isGetPromotion,
      isPromotedDiscounted: n.isPromotedDiscounted,
      salesPrice: n.typeSalesPrice,
      currentPrice: n.resolvedPrice,
      originalPrice: n.listOriginalPrice,
      showOriginalPrice: n.isDiscounted
    };
  }, []);

  const isItemPending = useCallback((productId, itemId = null) => {
    return pendingOperations.has(productId) || (itemId && pendingOperations.has(itemId));
  }, [pendingOperations]);

  return {
    cartItems: adjustedCartItems,
    cartTotals,
    isLoadingCart,
    isFetchingCart,
    isCartMutating,
    addProductToCart,
    handleQuantityChange,
    handleTypeChange,
    handleAddAsNewType,
    handlePriceChange,
    handleDiscountChange,
    handleDiscountCommentChange,
    handleDeleteItem,
    handleClearCart,
    getItemDetails,
    isItemPending,
    refetchCart,
    cacheProductCategory,
    cacheProductDisplay,
    bundleDiscounts,
    bundleLineDiscountByKey,
  };
};

